import {
  $AddressRegionDto,
  $CustomerTypeDto,
  $PriceSimulationCompensationTypeDto,
  $PriceSimulationEnergyTypeDto,
  $PriceSimulationHeatingTypeDto,
  $PriceSimulationHouseHoldSizeDto,
  $PriceSimulationHousingTypeDto,
  $PriceSimulationInstallationDto,
  $PriceSimulationMeterSubTypeDto,
  $SolarPanelInstallationDateDto,
} from '@eneco/dc-be/client';
import { PriceSimulationRenderingExtended } from '@sitecore/types/manual/be/PriceSimulationExtended';
import * as yup from 'yup';

import { PriceSimulationStep } from '../types/PriceSimulationSteps';
import '../../../utils/customYupValidation';

export const DEFAULT_INSTALLATION_TOTAL_KVA = 4.5;
const MAX_USAGE_VALUE_GAS = 100000;
const MAX_USAGE_VALUE_INSTALLATION_TOTAL_KVA = 10;
const MIN_USAGE_VALUE_TOTAL_INJECTION = 1;
export const MIN_PEAK_ENERGY_CONSUMPTION = 2.5;
export const MAX_PEAK_ENERGY_CONSUMPTION = 50;
export const MIN_USAGE_VALUE_INSTALLATION_TOTAL_KVA = 0;
const MAX_USAGE_VALUE_TOTAL_INJECTION = 999999;
const MAX_USAGE_VALUE_ENERGY_CONSUMPTION = 100000;

export const INSTALLATION_OPTIONS = [...$PriceSimulationInstallationDto.enum] as const;
export const CUSTOMER_TYPE_OPTIONS = [...$CustomerTypeDto.enum] as const;
export const HEATING_OPTIONS = [...$PriceSimulationHeatingTypeDto.enum] as const;
export const ENERGY_TYPE_OPTIONS = [...$PriceSimulationEnergyTypeDto.enum] as const;
export const HOUSING_TYPE_OPTIONS = [...$PriceSimulationHousingTypeDto.enum] as const;
export const NUMBER_OF_PEOPLE_IN_PROPERTY_OPTIONS = [...$PriceSimulationHouseHoldSizeDto.enum] as const;
export const METER_SUB_TYPE_REGISTER_OPTIONS = [...$PriceSimulationMeterSubTypeDto.enum] as const;
export const SOLAR_PANEL_INSTALL_DATE_OPTIONS = [...$SolarPanelInstallationDateDto.enum] as const;
export const COMPENSATION_TYPE_OPTIONS = [...$PriceSimulationCompensationTypeDto.enum] as const;
export const METER_TYPE_OPTIONS = {
  DIGITAL: 'digitalMeter',
  ANALOG: 'analogMeter',
  'BI-DIRECTIONAL': 'biDirectionalMeter',
} as const;
export const ELECTRICITY_CONSUMPTION_OPTIONS = {
  SINGLE: 'single',
  DAY: 'day',
  NIGHT: 'night',
  EXCLUSIVE_NIGHT: 'exclusiveNight',
} as const;
export const REGION_IN_BELGIUM_OPTIONS = [...$AddressRegionDto.enum] as const;

/**
 * Maps each step to its corresponding form fields that should be validated
 */
const STEP_TO_FIELDS_MAP: Record<PriceSimulationStep, string[]> = {
  prospectCheck: ['isProspect'],
  flowSelector: [],
  customerTypeCheck: ['customerType'],
  postalCodeCheck: ['postalCode', 'city', 'isUserDomiciledOnPostalCode', 'isApiPostalCodeCityValid'],
  energyTypeCheck: ['energyType'],
  currentEnergyConsumptionQuestion: ['currentEnergyConsumptionQuestion'],
  installationCheck: ['installationCheck'],
  heatingCheck: ['heatingCheck'],
  propertyTypeCheck: ['propertyTypeCheck'],
  houseHoldSize: ['houseHoldSize'],
  meterTypeCheck: ['meterType'],
  meterRegistersTypeCheck: ['meterSubTypeRegister', 'hasExclusiveNightMeter'],
  electricityConsumption: ['electricityConsumption'],
  peakEnergyConsumption: ['peakEnergyConsumption', 'isCheckedUseDefaultPeakConsumption'],
  gasConsumption: ['gasConsumption'],
  solarInstallDate: ['solarPanelInstallDate'],
  biDirectionalMeterStep: ['compensationType'],
  solarConsumption: [
    'solarConsumptionChoice',
    'installationTotalKva',
    'isCheckedUseDefaultInstallationTotalKva',
    'totalInjection',
    'peakHoursInjection',
    'offPeakHoursInjection',
  ],
  priceSimulationResult: [],
};

/**
 * Creates a form schema that only validates fields for steps in the dynamicStepOrder
 */
export const priceSimulationFormSchema = (
  fields: PriceSimulationRenderingExtended['fields'],
  dynamicStepOrder: PriceSimulationStep[] = [],
) => {
  // Collect all fields that should be included based on the step order
  const includedFields = new Set<string>();
  dynamicStepOrder.forEach(step => {
    STEP_TO_FIELDS_MAP[step]?.forEach(field => includedFields.add(field));
  });

  // Helper to check if a field should be included
  const shouldInclude = (fieldName: keyof PriceSimulationFormSchema) =>
    dynamicStepOrder.length === 0 || includedFields.has(fieldName);

  // Build schema object dynamically
  const schemaShape: Partial<Record<keyof PriceSimulationFormSchema, yup.AnySchema>> = {};

  if (shouldInclude('isProspect')) {
    schemaShape.isProspect = yup.boolean().required();
  }

  if (shouldInclude('customerType')) {
    schemaShape.customerType = yup.string().oneOf(CUSTOMER_TYPE_OPTIONS).required();
  }

  if (shouldInclude('postalCode')) {
    schemaShape.postalCode = yup
      .string()
      .required(fields.postalCodeSelectionStep.postalCodeFormField?.value?.requiredMessage);
  }
  if (shouldInclude('isApiPostalCodeCityValid')) {
    schemaShape.isApiPostalCodeCityValid = yup
      .boolean()
      .oneOf([true], fields.postalCodeSelectionStep.postalCodeFormField?.value?.validationMessage)
      .required(fields.postalCodeSelectionStep.postalCodeFormField?.value?.requiredMessage);
  }

  if (shouldInclude('energyType')) {
    schemaShape.energyType = yup.string().oneOf(ENERGY_TYPE_OPTIONS).required();
  }

  if (shouldInclude('currentEnergyConsumptionQuestion')) {
    schemaShape.currentEnergyConsumptionQuestion = yup.boolean().required();
  }

  if (shouldInclude('city')) {
    schemaShape.city = yup.string().required();
  }

  if (shouldInclude('installationCheck')) {
    schemaShape.installationCheck = yup
      .array()
      .of(yup.string().oneOf(INSTALLATION_OPTIONS))
      .min(1, fields.installationTypeStep.installationTypesList.value.requiredMessage)
      .required(fields.installationTypeStep.installationTypesList.value.requiredMessage);
  }

  if (shouldInclude('heatingCheck')) {
    schemaShape.heatingCheck = yup.string().oneOf(HEATING_OPTIONS).required();
  }

  if (shouldInclude('propertyTypeCheck')) {
    schemaShape.propertyTypeCheck = yup.string().oneOf(HOUSING_TYPE_OPTIONS).required();
  }

  if (shouldInclude('houseHoldSize')) {
    schemaShape.houseHoldSize = yup.string().oneOf(NUMBER_OF_PEOPLE_IN_PROPERTY_OPTIONS).required();
  }

  if (shouldInclude('gasConsumption')) {
    schemaShape.gasConsumption = yup
      .number()
      .integer(fields.gasConsumptionStep.gasConsumptionFormField.value.requiredMessage)
      .max(MAX_USAGE_VALUE_GAS, fields.gasConsumptionStep.gasConsumptionFormField.value.validationMessage)
      .typeError(fields.gasConsumptionStep.gasConsumptionFormField.value.requiredMessage)
      .required(fields.gasConsumptionStep.gasConsumptionFormField.value.requiredMessage);
  }

  if (shouldInclude('meterType')) {
    schemaShape.meterType = yup
      .string()
      .oneOf(Object.values(METER_TYPE_OPTIONS))
      .required(fields.digitalMeterTypeStep.meterTypeList.value.requiredMessage);
  }

  if (shouldInclude('meterSubTypeRegister')) {
    schemaShape.meterSubTypeRegister = yup.string().oneOf(METER_SUB_TYPE_REGISTER_OPTIONS).required();
  }

  if (shouldInclude('hasExclusiveNightMeter')) {
    schemaShape.hasExclusiveNightMeter = yup
      .boolean()
      .required(fields.digitalMeterTypeStep.nightMeterList.value.requiredMessage);
  }

  if (shouldInclude('solarPanelInstallDate')) {
    schemaShape.solarPanelInstallDate = yup.string().oneOf(SOLAR_PANEL_INSTALL_DATE_OPTIONS).required();
  }

  if (shouldInclude('compensationType')) {
    schemaShape.compensationType = yup
      .string()
      .oneOf(COMPENSATION_TYPE_OPTIONS)
      .required(fields.biDirectionalMeterStep.compensationOptionsList.value.requiredMessage);
  }

  if (shouldInclude('electricityConsumption')) {
    schemaShape.electricityConsumption = yup
      .object({
        single: yup
          .number()
          .integer(fields.electricityConsumptionStep?.consumptionSingleMeterFormField?.value.validationMessage)
          .typeError(fields.electricityConsumptionStep?.consumptionSingleMeterFormField?.value.validationMessage)
          .nullable(),
        day: yup
          .number()
          .integer(fields.electricityConsumptionStep?.consumptionPeakHoursFormField?.value.validationMessage)
          .typeError(fields.electricityConsumptionStep?.consumptionPeakHoursFormField?.value.validationMessage)
          .nullable(),
        night: yup
          .number()
          .integer(fields.electricityConsumptionStep?.consumptionOffPeakHoursFormField?.value.validationMessage)
          .typeError(fields.electricityConsumptionStep?.consumptionOffPeakHoursFormField?.value.validationMessage)
          .nullable(),
        exclusiveNight: yup
          .number()
          .integer(fields.electricityConsumptionStep?.consumptionExcludingNightHoursFormField?.value.validationMessage)
          .typeError(
            fields.electricityConsumptionStep?.consumptionExcludingNightHoursFormField?.value.validationMessage,
          )
          .nullable(),
      })
      .test(
        'at-least-one-value',
        fields.electricityConsumptionStep?.consumptionSingleMeterFormField?.value.requiredMessage,
        function (value) {
          if (!value) return false;

          const meterSubType = this.parent.meterSubTypeRegister;
          const hasExclusiveNightMeter = this.parent.hasExclusiveNightMeter;
          const errors: yup.ValidationError[] = [];

          if (meterSubType.toLowerCase() === ELECTRICITY_CONSUMPTION_OPTIONS.SINGLE) {
            if (!value.single) {
              errors.push(
                this.createError({
                  path: `${this.path}.single`,
                  message: fields.electricityConsumptionStep?.consumptionSingleMeterFormField?.value.requiredMessage,
                }),
              );
            }
            if (Number(value.single) > MAX_USAGE_VALUE_ENERGY_CONSUMPTION) {
              errors.push(
                this.createError({
                  path: `${this.path}.single`,
                  message: fields.electricityConsumptionStep?.maxConsumptionUsageDescription?.value,
                }),
              );
            }
          } else {
            if (!value.day) {
              errors.push(
                this.createError({
                  path: `${this.path}.day`,
                  message: fields.electricityConsumptionStep?.consumptionPeakHoursFormField?.value.requiredMessage,
                }),
              );
            }

            if (!value.night) {
              errors.push(
                this.createError({
                  path: `${this.path}.night`,
                  message: fields.electricityConsumptionStep?.consumptionOffPeakHoursFormField?.value.requiredMessage,
                }),
              );
            }

            if (Number(value.day) + Number(value.night) > MAX_USAGE_VALUE_ENERGY_CONSUMPTION) {
              errors.push(
                this.createError({
                  path: `${this.path}.day`,
                  message: fields.electricityConsumptionStep?.maxConsumptionUsageDescription?.value,
                }),
              );

              errors.push(
                this.createError({
                  path: `${this.path}.night`,
                  message: fields.electricityConsumptionStep?.maxConsumptionUsageDescription?.value,
                }),
              );
            }
          }

          if (hasExclusiveNightMeter && !value.exclusiveNight) {
            errors.push(
              this.createError({
                path: `${this.path}.exclusiveNight`,
                message:
                  fields.electricityConsumptionStep?.consumptionExcludingNightHoursFormField?.value.requiredMessage,
              }),
            );
            if (Number(value.exclusiveNight) > MAX_USAGE_VALUE_ENERGY_CONSUMPTION) {
              errors.push(
                this.createError({
                  path: `${this.path}.exclusiveNight`,
                  message: fields.electricityConsumptionStep?.maxConsumptionUsageDescription?.value,
                }),
              );
            }
          }

          if (
            Number(value.single) + Number(value.day) + Number(value.night) + Number(value.exclusiveNight) >
            MAX_USAGE_VALUE_ENERGY_CONSUMPTION
          ) {
            if (meterSubType.toLowerCase() === ELECTRICITY_CONSUMPTION_OPTIONS.SINGLE) {
              errors.push(
                this.createError({
                  path: `${this.path}.single`,
                  message: fields.electricityConsumptionStep?.maxConsumptionUsageDescription?.value,
                }),
              );
            } else {
              errors.push(
                this.createError({
                  path: `${this.path}.day`,
                  message: fields.electricityConsumptionStep?.maxConsumptionUsageDescription?.value,
                }),
              );

              errors.push(
                this.createError({
                  path: `${this.path}.night`,
                  message: fields.electricityConsumptionStep?.maxConsumptionUsageDescription?.value,
                }),
              );
            }
            errors.push(
              this.createError({
                path: `${this.path}.exclusiveNight`,
                message: fields.electricityConsumptionStep?.maxConsumptionUsageDescription?.value,
              }),
            );
          }

          if (errors.length > 0) {
            return new yup.ValidationError(errors);
          }

          return true;
        },
      );
  }

  if (shouldInclude('peakEnergyConsumption')) {
    schemaShape.peakEnergyConsumption = yup
      .string()
      .validDecimal(
        fields.capacityRateStep.capacityRateFormField?.value.validationMessage,
        MIN_PEAK_ENERGY_CONSUMPTION,
        MAX_PEAK_ENERGY_CONSUMPTION,
      )
      .required(fields.capacityRateStep?.capacityRateFormField?.value.requiredMessage);
  }

  if (shouldInclude('isCheckedUseDefaultPeakConsumption')) {
    schemaShape.isCheckedUseDefaultPeakConsumption = yup.boolean();
  }

  // Solar consumption choice (kva or kwh)
  if (shouldInclude('solarConsumptionChoice')) {
    schemaShape.solarConsumptionChoice = yup.string().oneOf(['kva', 'kwh']).nullable();
  }

  if (shouldInclude('installationTotalKva')) {
    schemaShape.installationTotalKva = yup
      .string()
      .nullable()
      .when('solarConsumptionChoice', {
        is: 'kva',
        then: schema =>
          schema
            .validDecimal(
              fields.solarPanelsEnergyReturnStep?.injectionKvaFormField?.value.validationMessage,
              MIN_USAGE_VALUE_INSTALLATION_TOTAL_KVA,
              MAX_USAGE_VALUE_INSTALLATION_TOTAL_KVA,
            )
            .required(fields.solarPanelsEnergyReturnStep?.injectionKvaFormField?.value.requiredMessage),
        otherwise: schema => schema,
      });
  }

  if (shouldInclude('isCheckedUseDefaultInstallationTotalKva')) {
    schemaShape.isCheckedUseDefaultInstallationTotalKva = yup.boolean();
  }

  if (shouldInclude('totalInjection')) {
    schemaShape.totalInjection = yup
      .number()
      .integer(fields.solarPanelsEnergyReturnStep?.injectionSingleFormField?.value.requiredMessage)
      .nullable()
      .when(['solarConsumptionChoice', 'meterSubTypeRegister'], {
        is: (choice: string, meterSubType: string) => choice === 'kwh' && meterSubType === 'Single',
        then: schema =>
          schema
            .integer(fields.solarPanelsEnergyReturnStep?.injectionSingleFormField?.value.validationMessage)
            .min(
              MIN_USAGE_VALUE_TOTAL_INJECTION,
              fields.solarPanelsEnergyReturnStep?.injectionSingleFormField?.value.validationMessage,
            )
            .max(
              MAX_USAGE_VALUE_TOTAL_INJECTION,
              fields.solarPanelsEnergyReturnStep?.injectionSingleFormField?.value.validationMessage,
            )
            .typeError(fields.solarPanelsEnergyReturnStep?.injectionSingleFormField?.value.requiredMessage)
            .required(fields.solarPanelsEnergyReturnStep?.injectionSingleFormField?.value.requiredMessage),
        otherwise: schema => schema,
      });
  }

  if (shouldInclude('peakHoursInjection')) {
    schemaShape.peakHoursInjection = yup
      .number()
      .integer(fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.requiredMessage)
      .nullable()
      .when(['solarConsumptionChoice', 'meterSubTypeRegister'], {
        is: (choice: string, meterSubType: string) => choice === 'kwh' && meterSubType !== 'Single',
        then: schema =>
          schema
            .integer(fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.validationMessage)
            .min(
              MIN_USAGE_VALUE_TOTAL_INJECTION,
              fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.validationMessage,
            )
            .max(
              MAX_USAGE_VALUE_TOTAL_INJECTION,
              fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.validationMessage,
            )
            .typeError(fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.requiredMessage)
            .required(fields.solarPanelsEnergyReturnStep.injectionPeakHoursFormField.value.requiredMessage),
        otherwise: schema => schema,
      });
  }

  if (shouldInclude('offPeakHoursInjection')) {
    schemaShape.offPeakHoursInjection = yup
      .number()
      .integer(fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.requiredMessage)
      .nullable()
      .when(['solarConsumptionChoice', 'meterSubTypeRegister'], {
        is: (choice: string, meterSubType: string) => choice === 'kwh' && meterSubType !== 'Single',
        then: schema =>
          schema
            .integer(fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.validationMessage)
            .min(
              MIN_USAGE_VALUE_TOTAL_INJECTION,
              fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.validationMessage,
            )
            .max(
              MAX_USAGE_VALUE_TOTAL_INJECTION,
              fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.validationMessage,
            )
            .typeError(fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.requiredMessage)
            .required(fields.solarPanelsEnergyReturnStep.injectionOffPeakHoursFormField.value.requiredMessage),
        otherwise: schema => schema,
      });
  }

  // Region is always included as it may be set automatically
  schemaShape.regionInBelgium = yup.string().oneOf(REGION_IN_BELGIUM_OPTIONS).nullable();

  return yup.object(schemaShape);
};

/**
 * Type definition for the complete price simulation form
 * Note: Some fields may be optional/excluded based on the dynamicStepOrder
 */
export type PriceSimulationFormSchema = {
  isProspect?: boolean;
  customerType?: (typeof CUSTOMER_TYPE_OPTIONS)[number];
  postalCode?: string;
  isUserDomiciledOnPostalCode?: boolean;
  isApiPostalCodeCityValid?: boolean;
  energyType?: (typeof ENERGY_TYPE_OPTIONS)[number];
  currentEnergyConsumptionQuestion?: boolean;
  city?: string;
  installationCheck?: (typeof INSTALLATION_OPTIONS)[number][];
  heatingCheck?: (typeof HEATING_OPTIONS)[number];
  propertyTypeCheck?: (typeof HOUSING_TYPE_OPTIONS)[number];
  houseHoldSize?: (typeof NUMBER_OF_PEOPLE_IN_PROPERTY_OPTIONS)[number];
  gasConsumption?: number;
  meterType?: (typeof METER_TYPE_OPTIONS)[keyof typeof METER_TYPE_OPTIONS];
  meterSubTypeRegister?: (typeof METER_SUB_TYPE_REGISTER_OPTIONS)[number];
  hasExclusiveNightMeter?: boolean;
  solarPanelInstallDate?: (typeof SOLAR_PANEL_INSTALL_DATE_OPTIONS)[number];
  compensationType?: (typeof COMPENSATION_TYPE_OPTIONS)[number];
  electricityConsumption?: {
    single?: number | null;
    day?: number | null;
    night?: number | null;
    exclusiveNight?: number | null;
  };
  peakEnergyConsumption?: string;
  isCheckedUseDefaultPeakConsumption?: boolean;
  solarConsumptionChoice?: 'kva' | 'kwh' | null;
  installationTotalKva?: number | null;
  isCheckedUseDefaultInstallationTotalKva?: boolean;
  totalInjection?: number | null;
  peakHoursInjection?: number | null;
  offPeakHoursInjection?: number | null;
  regionInBelgium?: (typeof REGION_IN_BELGIUM_OPTIONS)[number] | null;
};
